import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { OtpCardTab } from "./OtpCardTab";
import { IntesaCardTab } from "./IntesaCardTab";
import { RaiffeisenCardTab } from "./RaiffeisenCardTab";

/**
 * Objedinjeni tab "Kartice" (04.10.2026, zahtev korisnika - ranije su OTP,
 * Intesa i Raiffeisen bili TRI odvojena taba u Podešavanjima firme).
 *
 * Raspored (04.10.2026, 14:06 - posle slika i objašnjenja):
 *   - NIJEDAN modul aktivan → sve tri kartice jedna pored druge.
 *   - JEDAN (ili više) modul aktivan → ta kartica glavni prozor levo,
 *     preostale (neaktivne) jedna ispod druge desno.
 *
 * Vizuelna doterivanja (14:16 - korisnik poslao poređenje "ovako je ružno
 * / ovako je lepo"): "zaključane" kartice (ModuleGateNotice) imaju
 * SOPSTVENI border+pozadinu - omotavanje u još jednu BankCard kutiju
 * pravilo je "kutija u kutiji" (dupla ivica, siva pozadina unutar bele),
 * što je delovalo nagurano, pogotovo u uskoj desnoj koloni. Zato neaktivne
 * banke sada NEMAJU spoljnu BankCard kutiju - samo obojena traka iznad
 * ModuleGateNotice-ove sopstvene, već zaokružene kutije. Aktivna banka
 * (prava forma, bez sopstvenog border-a) i dalje dobija punu BankCard
 * kutiju, jer njoj taj okvir treba.
 *
 * Koji je modul aktivan se saznaje preko GET /company/modules
 * (api.getMyCompanyModulesForWeb, isti poziv koji već koristi Sidebar).
 * Same OtpCardTab/IntesaCardTab/RaiffeisenCardTab komponente NISU menjane
 * iznutra - i dalje same čuvaju podatke i same prikazuju ModuleGateNotice.
 */
const BANKS = [
  { key: "otp_card", accent: "bg-emerald-500", Component: OtpCardTab },
  { key: "intesa_card", accent: "bg-blue-500", Component: IntesaCardTab },
  { key: "raiffeisen_card", accent: "bg-amber-500", Component: RaiffeisenCardTab },
] as const;

function ActiveBankCard({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-surface shadow-card">
      <div className={`h-1.5 ${accent}`} />
      <div className="p-5">{children}</div>
    </div>
  );
}

function InactiveBankSlot({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={`h-1.5 rounded-t-xl ${accent}`} />
      {children}
    </div>
  );
}

export function CardPaymentsTab() {
  const { data: modulesResponse, isLoading } = useQuery({
    queryKey: ["my-company-modules-web"],
    queryFn: () => api.getMyCompanyModulesForWeb(),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  const activeKeys = new Set(modulesResponse?.modules.map((m) => m.key) ?? []);
  const activeBanks = BANKS.filter((b) => activeKeys.has(b.key));
  const inactiveBanks = BANKS.filter((b) => !activeKeys.has(b.key));

  // Nijedna banka aktivna - sve tri u redu.
  if (activeBanks.length === 0) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        {BANKS.map(({ key, accent, Component }) => (
          <InactiveBankSlot key={key} accent={accent}>
            <Component />
          </InactiveBankSlot>
        ))}
      </div>
    );
  }

  // Jedna ili više aktivnih - glavni prozor levo, preostale (neaktivne)
  // jedna ispod druge desno.
  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <div className="flex-1 space-y-6 lg:max-w-xl">
        {activeBanks.map(({ key, accent, Component }) => (
          <ActiveBankCard key={key} accent={accent}>
            <Component />
          </ActiveBankCard>
        ))}
      </div>
      {inactiveBanks.length > 0 && (
        <div className="flex flex-col gap-6 lg:w-72">
          {inactiveBanks.map(({ key, accent, Component }) => (
            <InactiveBankSlot key={key} accent={accent}>
              <Component />
            </InactiveBankSlot>
          ))}
        </div>
      )}
    </div>
  );
}
